<script setup>
import { computed, defineEmits, defineProps, onMounted, ref, watch } from "vue";
import dayjs from "dayjs";
import ProductService from "@/services/ProductService";

const props = defineProps({
  calculatedTimes: { type: Boolean, default: false },
  editable: { type: Boolean, required: false },
  entries: { type: Object, required: true },
  loading: { type: Boolean, required: false, default: false },
  defaultLocationAddress: { type: String, required: false, default: null },
  manageLocationInBasics: { type: Boolean, required: false, default: false },
});

const emit = defineEmits(["update:entries"]);

const products = ref([]);
const dateTime = ref({
  startDate: new Date().toISOString().slice(0, 10),
  finishDate: new Date().toISOString().slice(0, 10),
  startTime: "08:00:00",
  finishTime: "18:00:00",
  qty: 1,
  product: null,
});

const cloneEntries = (val) => JSON.parse(JSON.stringify(val ?? { dates: [] }));
const normalizeLocationAddress = (value) =>
  (value ?? "").toString().trim().replace(/\s+/g, " ");
const defaultLocationAddress = computed(
  () => normalizeLocationAddress(props.defaultLocationAddress) || null,
);

const localEntries = ref(cloneEntries(props.entries));
const sortedDates = computed(() =>
  Array.isArray(localEntries.value?.dates)
    ? [...localEntries.value.dates].sort((a, b) =>
        String(a?.date ?? "").localeCompare(String(b?.date ?? "")) ||
        String(a?.start_time ?? "").localeCompare(String(b?.start_time ?? "")) ||
        String(a?.finish_time ?? "").localeCompare(String(b?.finish_time ?? "")),
      )
    : [],
);

watch(
  () => props.entries,
  (val) => {
    localEntries.value = cloneEntries(val);
  },
  { deep: true, immediate: true },
);

const commitEntries = () => {
  emit("update:entries", cloneEntries(localEntries.value));
};

onMounted(() => {
  loadProducts();
});

async function loadProducts() {
  const svc = new ProductService();
  products.value = await svc.index();
  dateTime.value.product = products.value.length ? products.value[0].id : null;
}

const getDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  let startDateTime = dayjs(
    `${dayjs().format("YYYY-MM-DD")} ${startTime}`,
    "YYYY-MM-DD HH:mm",
  );
  let endDateTime = dayjs(
    `${dayjs().format("YYYY-MM-DD")} ${endTime}`,
    "YYYY-MM-DD HH:mm",
  );

  if (endTime < startTime) {
    endDateTime = endDateTime.add(1, "day");
  }

  return endDateTime.diff(startDateTime, "hour");
};

const getProductName = (productId) => {
  return products.value.find((p) => p.id === productId)?.name ?? "Unknown Unit";
};

const getEntryRenderKey = (entry, entryIndex = null) =>
  entry.id ??
  `${entry.date}-${entryIndex ?? 0}`;

const getResourceRenderKey = (entry, resource, resourceIndex = null) =>
  resource.id ??
  `${getEntryRenderKey(entry)}-${resourceIndex ?? 0}`;

function addEntry() {
  if (dateTime.value.finishDate < dateTime.value.startDate) {
    dateTime.value.finishDate = dateTime.value.startDate;
  }

  const {
    startDate,
    finishDate,
    startTime,
    finishTime,
    qty,
    product: prodId,
  } = dateTime.value;
  const baseProduct = products.value.find((p) => p.id === prodId);
  if (!baseProduct) return;

  if (!localEntries.value.dates) {
    localEntries.value.dates = [];
  }

  for (
    let d = new Date(startDate);
    d <= new Date(finishDate);
    d.setDate(d.getDate() + 1)
  ) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const date = `${yyyy}-${mm}-${dd}`;
    const existingDate = localEntries.value.dates.find(
      (candidate) => candidate?.date === date,
    );
    const fallbackLocationAddress = defaultLocationAddress.value;

    const entry = existingDate ?? {
      date,
      id: null,
      start_time: startTime,
      finish_time: finishTime,
      location_name: null,
      location_address: fallbackLocationAddress,
      resources: [],
    };

    if (!existingDate) {
      localEntries.value.dates.push(entry);
    } else if (!entry.location_address && fallbackLocationAddress) {
      entry.location_address = fallbackLocationAddress;
      entry.location_name = null;
    }

    addResource(entry, baseProduct.id, qty);
  }
  commitEntries();
}

function removeEntry(dateEntry) {
  localEntries.value.dates = localEntries.value.dates.filter(
    (d) => d !== dateEntry,
  );
  commitEntries();
}

function addResource(entry, productId = null, quantity = 1) {
  if (!Array.isArray(entry.resources)) entry.resources = [];
  const requestedProductId = Number(productId ?? dateTime.value.product);
  const existingResource = entry.resources.find(
    (resource) =>
      Number(resource?.product_id ?? resource?.product?.id) ===
      requestedProductId,
  );
  if (existingResource && productId !== null) {
    existingResource.quantity =
      Math.max(1, Number(existingResource.quantity) || 1) +
      Math.max(1, Number(quantity) || 1);
    commitEntries();
    return;
  }

  const usedProductIds = new Set(
    entry.resources.map((resource) =>
      Number(resource?.product_id ?? resource?.product?.id),
    ),
  );
  const baseProduct = productId !== null
    ? products.value.find((product) => Number(product.id) === requestedProductId)
    : products.value.find((product) => !usedProductIds.has(Number(product.id)));
  if (!baseProduct) return;

  entry.resources.push({
    product: baseProduct,
    product_id: baseProduct.id,
    quantity: Math.max(1, Number(quantity) || 1),
  });
  commitEntries();
}

function removeResource(resource) {
  for (const dateEntry of localEntries.value.dates) {
    const idx = dateEntry.resources.indexOf(resource);
    if (idx !== -1) {
      dateEntry.resources.splice(idx, 1);
      break;
    }
  }
  commitEntries();
}

function changeProduct(resource, productId) {
  const base = products.value.find((p) => p.id === Number(productId));
  if (!base) return;
  const entry = localEntries.value.dates.find((date) =>
    date.resources.includes(resource),
  );
  const existing = entry?.resources.find(
    (candidate) =>
      candidate !== resource &&
      Number(candidate?.product_id ?? candidate?.product?.id) === Number(base.id),
  );
  if (existing && entry) {
    existing.quantity =
      Math.max(1, Number(existing.quantity) || 1) +
      Math.max(1, Number(resource.quantity) || 1);
    entry.resources = entry.resources.filter((candidate) => candidate !== resource);
    commitEntries();
    return;
  }
  resource.product = base;
  resource.product_id = base.id;
  commitEntries();
}

const locationDisplay = (entry) =>
  entry?.location_address ||
  entry?.location_name ||
  (props.manageLocationInBasics ? "Set location in Event Basics" : null);
</script>

<template>
  <FormBox header="Resources" v-show="!loading">
    <div class="space-y-8">
      <section
        v-show="editable"
        class="bg-white dark:bg-gray-900 rounded shadow-md shadow-gray-300 dark:shadow-md dark:shadow-gray-600/50 p-2 space-y-4"
      >
        <form class="hidden 2xl:grid grid-cols-6 gap-4 text-sm" @submit.prevent="addEntry">
          <div>
            <label class="block font-medium mb-1">Start Date</label>
            <input
              type="date"
              v-model="dateTime.startDate"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            />
          </div>
          <div>
            <label class="block font-medium mb-1">End Date</label>
            <input
              type="date"
              v-model="dateTime.finishDate"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            />
          </div>
          <div>
            <label class="block font-medium mb-1">Start Time</label>
            <input
              type="time"
              v-model="dateTime.startTime"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            />
          </div>
          <div>
            <label class="block font-medium mb-1">End Time</label>
            <input
              type="time"
              v-model="dateTime.finishTime"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            />
          </div>
          <div>
            <label class="block font-medium mb-1">Unit Type</label>
            <select
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              v-model="dateTime.product"
            >
              <option value="">-- Select Unit --</option>
              <option
                :key="product.id"
                v-for="product in products"
                :value="product.id"
              >
                {{ product.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block font-medium mb-1">Unit Qty</label>
            <input type="number" min="1" step="1" v-model.number="dateTime.qty" />
          </div>
        </form>

        <form class="2xl:hidden grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm" @submit.prevent="addEntry">
          <div>
            <label class="block font-medium mb-1">Start Date</label>
            <input
              type="date"
              v-model="dateTime.startDate"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            />
          </div>
          <div>
            <label class="block font-medium mb-1">End Date</label>
            <input
              type="date"
              v-model="dateTime.finishDate"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            />
          </div>
          <div>
            <label class="block font-medium mb-1">Unit Type</label>
            <select
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              v-model="dateTime.product"
            >
              <option value="">-- Select Unit --</option>
              <option
                :key="product.id"
                v-for="product in products"
                :value="product.id"
              >
                {{ product.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block font-medium mb-1">Start Time</label>
            <input
              type="time"
              v-model="dateTime.startTime"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            />
          </div>
          <div>
            <label class="block font-medium mb-1">End Time</label>
            <input
              type="time"
              v-model="dateTime.finishTime"
              class="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
            />
          </div>
          <div>
            <label class="block font-medium mb-1">Unit Qty</label>
            <input type="number" min="1" step="1" v-model.number="dateTime.qty" class="w-full" />
          </div>
        </form>

        <div v-if="manageLocationInBasics" class="text-xs text-slate-500">
          Location is managed in Event Basics and applied from the matching
          Venue &amp; Schedule date.
        </div>

        <button
          class="mt-2 bg-m1 text-white px-4 py-2 rounded"
          @click.prevent="addEntry"
        >
          Add Entries
        </button>
      </section>

      <div
        class="bg-white dark:bg-gray-900 rounded shadow-md shadow-gray-300 dark:shadow-md dark:shadow-gray-600/50 p-2 space-y-6"
        v-for="(entry, entryIndex) in sortedDates"
        :key="getEntryRenderKey(entry, entryIndex)"
      >
        <div>
          <div class="flex justify-between items-center">
            <h2 class="font-semibold">
              🗓️ {{ dayjs(entry.date).format("dddd DD MMM YYYY") }}
            </h2>
            <button
              v-if="editable"
              class="text-red-500 text-sm hover:underline btn del"
              @click.prevent="removeEntry(entry)"
            >
              Remove Day
            </button>
          </div>
          <div class="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div
              v-if="locationDisplay(entry)"
              class="text-slate-600 dark:text-slate-300 md:col-span-2"
            >
              {{ locationDisplay(entry) }}
            </div>
            <div
              v-if="editable && manageLocationInBasics"
              class="text-xs text-slate-400 md:col-span-2"
            >
              Edit locations in Event Basics.
            </div>
          </div>
          <div class="my-2 flex flex-wrap items-center gap-2 text-sm">
            <template v-if="editable && !calculatedTimes">
              <input
                class="rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700"
                type="time"
                :value="entry.start_time"
                @input="
                  (event) => {
                    entry.start_time = event.target.value;
                    commitEntries();
                  }
                "
              />
              <span class="text-slate-400">to</span>
              <input
                class="rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700"
                type="time"
                :value="entry.finish_time"
                @input="
                  (event) => {
                    entry.finish_time = event.target.value;
                    commitEntries();
                  }
                "
              />
            </template>
            <template v-else>
              <span class="font-medium text-slate-900 dark:text-slate-100">
                {{ entry.start_time ?? "Start time not set" }}
              </span>
              <span class="text-slate-400">to</span>
              <span class="font-medium text-slate-900 dark:text-slate-100">
                {{ entry.finish_time ?? "Finish time not set" }}
              </span>
            </template>
            <span class="text-gray-500"
              >({{
                getDuration(entry.start_time, entry.finish_time)
              }}
              hrs)</span
            >
          </div>
          <button
            v-if="editable"
            class="mt-1 text-blue-600 text-sm hover"
            @click.prevent="addResource(entry)"
          >
            + Add Another Unit
          </button>

          <div
            v-if="entry.resources.length"
            class="hidden md:grid md:grid-cols-[minmax(0,1fr)_minmax(110px,0.26fr)_auto] md:gap-3 md:px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400"
          >
            <div>Unit Type</div>
            <div>Qty</div>
            <div v-if="editable" class="text-right">Action</div>
          </div>
        </div>

        <div
          class="grid grid-cols-1 gap-2 text-sm mb-3 md:grid-cols-[minmax(0,1fr)_minmax(110px,0.26fr)_auto] md:items-end"
          v-for="(resource, resourceIndex) in entry.resources"
          :key="getResourceRenderKey(entry, resource, resourceIndex)"
        >
          <div class="min-w-0">
            <span v-if="!editable">{{
              getProductName(resource.product_id)
            }}</span>
            <select
              v-else
              class="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700"
              v-model="resource.product_id"
              @change="changeProduct(resource, $event.target.value)"
            >
              <option
                :key="product.id"
                v-for="product in products"
                :value="product.id"
              >
                {{ product.name }}
              </option>
            </select>
          </div>
          <div class="min-w-0">
            <input
              class="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700"
              type="number"
              min="1"
              step="1"
              v-model.number="resource.quantity"
              :disabled="!editable"
              @input="commitEntries"
            />
          </div>
          <div class="flex items-end justify-end" v-if="editable">
            <button
              class="btn del"
              @click.prevent="removeResource(resource)"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </FormBox>
</template>
